import { supabase } from '../lib/supabase';
import type { Calendar } from '../types/calendar';
import { CalendarBanner } from '../types/banner';

interface UpdateCalendarParams {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  googleCalendarUrl: string;
  categoryId: string;
  demoVideoUrl: string | null;
  isPublic: boolean;
  banner: CalendarBanner;
}

export async function updateCalendar({
  id,
  userId,
  name,
  description,
  googleCalendarUrl,
  categoryId,
  demoVideoUrl,
  isPublic,
  banner
}: UpdateCalendarParams) {
  const { error } = await supabase
    .from('calendars')
    .update({
      name: name.trim(),
      description: description?.trim() || null,
      google_calendar_url: googleCalendarUrl.trim(),
      category_id: categoryId,
      demo_video_url: demoVideoUrl?.trim() || null,
      is_public: isPublic,
      banner
    })
    .eq('id', id)
    .eq('user_id', userId);

  if (error) throw error;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export async function getTopCalendars(limit = 6): Promise<Calendar[]> {
  let retries = 0;
  
  while (retries < MAX_RETRIES) {
    try {
      // First get calendar IDs with their stats
      const { data: statsData } = await supabase
        .from('calendar_stats')
        .select('calendar_id, subscriber_count')
        .order('subscriber_count', { ascending: false })
        .limit(limit);

      if (!statsData?.length) {
        return [];
      }

      const calendarIds = statsData.map(stat => stat.calendar_id);

      // Then get the calendar details
      const { data: calendars, error } = await supabase
        .from('calendars')
        .select(`
          *,
          profiles!calendars_user_id_fkey (
            display_name
          ),
          calendar_stats!inner (
            subscriber_count
          )
        `)
        .eq('is_public', true)
        .in('id', calendarIds)
        .order('calendar_stats(subscriber_count)', { ascending: false })
        .limit(limit);

      if (error) throw error;

      // Transform the data to include subscriber count directly
      return (calendars || []).map(calendar => ({
        ...calendar,
        subscriber_count: calendar.calendar_stats?.[0]?.subscriber_count || 0
      }));
    } catch (error) {
      retries++;
      console.warn(`Attempt ${retries} failed:`, error);
      
      if (retries === MAX_RETRIES) {
        console.error('Error fetching top calendars:', error);
        return []; // Return empty array after all retries fail
      }

      // Wait before retrying with exponential backoff
      await wait(RETRY_DELAY * Math.pow(2, retries - 1));
    }
  }

  return []; // Fallback return
}

export async function getActiveCalendars(limit = 6): Promise<(Calendar & { upcoming_event_count: number })[]> {
  try {
    const now = new Date().toISOString();

    const { data, error } = await supabase
      .from('calendars')
      .select(`
        *,
        profiles!calendars_user_id_fkey (
          display_name
        ),
        calendar_events!inner (
          id
        )
      `)
      .eq('is_public', true)
      .gte('calendar_events.start_datetime', now)
      .limit(200);

    if (error) throw error;

    const counted = (data || []).map(cal => ({
      ...cal,
      upcoming_event_count: Array.isArray(cal.calendar_events) ? cal.calendar_events.length : 0,
    }));

    counted.sort((a, b) => b.upcoming_event_count - a.upcoming_event_count);

    return counted.slice(0, limit);
  } catch (error) {
    console.error('Error fetching active calendars:', error);
    return [];
  }
}